import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { groupByUser, type RawMeeting } from "./MeetingDashboard";
import { MeetingCardMain } from "@/components/Cards/MeetingCardMain";
import { useDashboardTheme, getBadgeTheme } from "./themeHelper";
import PitchCardMain from "@/components/Cards/PitchCardMain";
import RepScriptsTab from "./RepScriptsTab";
import { usePitchDashboardData } from "@/hooks/usePitchDashboardData";
import { useScriptDashboardOverview } from "@/hooks/useTenantDashboardService";
import {
  generateRepCoachingInsights,
  getRepCoachingInsights,
  checkScenarioMatch,
} from "@/api/tenantDashboardService";
import {
  evaluateRepInsightsEligibility,
} from "@/utils/repInsightsEligibility";
import useAuthStore from "@/store/useAuthStore";
import { useUserStore } from "@/store";
import scenarioService from "@/api/scenarioService";

interface RepDashboardPageProps {
  rep: any;
  userData: any;
  rawMeetings: RawMeeting[];
  onBack: () => void;
  meetingsLoading?: boolean;
}
type TabKey = "overview" | "meetings" | "pitches" | "scripts";
const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "meetings", label: "Meetings" },
  { key: "pitches", label: "Pitches" },
  { key: "scripts", label: "Scripts" },
];

function getInitials(name: string = "") {
  return name
    .trim()
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
const AVATAR_COLORS = [
  "#6C63FF",
  "#FF6584",
  "#43C6AC",
  "#F7971E",
  "#56CCF2",
  "#BB6BD9",
  "#F2994A",
  "#27AE60",
];
function avatarColor(name: string = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function avg(arr: number[]): number {
  const v = arr.filter((x) => typeof x === "number" && !isNaN(x));
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0;
}
function fmtDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function skillIconForKey(key: string) {
  const map: Record<string, string> = {
    discovery: "🔎",
    pitch_delivery: "🎤",
    confidence_and_tone: "🎙️",
    engagement: "🤝",
    closing: "🔐",
    opening: "👋",
  };
  return map[key] || "⭐";
}

const isWithinDateRange = (dateStr: any, range: string): boolean => {
  if (range === "all") return true;
  if (!dateStr) return false;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return false;
  
  const limitDays = Number(range);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - limitDays);
  return date.getTime() >= cutoff.getTime();
};

function ArrowBtn({ onClick }: { onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 28, height: 28, borderRadius: 8,
        background: hov ? "rgba(108,99,255,0.3)" : "rgba(108,99,255,0.12)",
        border: "1px solid rgba(108,99,255,0.25)",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0, transition: "all 0.2s",
        color: "#A78BFA", fontSize: 14, fontWeight: 700,
      }}
    >›</button>
  );
}



function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 14,
        padding: 60,
        textAlign: "center",
        color: "rgba(255,255,255,0.5)",
        fontSize: 14,
      }}
    >
      {message}
    </div>
  );
}

/* ─── Score extraction helpers ─────────────────────────────────────────── */

function extractCategoryScore(scoreObj: any, category: string): number {
  if (!scoreObj) return 0;
  const cat = scoreObj[category];
  if (!cat) return 0;
  const vals = Object.values(cat).filter(
    (v) => typeof v === "number"
  ) as number[];
  if (!vals.length) return 0;
  const maxPerField = 5;
  return Math.min(100, Math.round((avg(vals) / maxPerField) * 100));
}

function computeSkillScoresFromPitches(pitches: any[], meetings: any[]) {
  const categories = [
    "discovery",
    "pitch_delivery",
    "confidence_and_tone",
    "engagement",
    "closing",
    "opening",
  ];
  const result: Record<string, number> = {};

  categories.forEach((cat) => {
    const scores: number[] = [];
    pitches.forEach((p) => {
      const s =
        p.overallScore?.overall_percentange !== undefined
          ? p.overallScore
          : p.categoryScores?.scores;
      if (s) scores.push(extractCategoryScore(s, cat));
    });
    meetings.forEach((m) => {
      const s = m.ai_score;
      if (s) scores.push(extractCategoryScore(s, cat));
    });
    result[cat] = scores.length ? Math.round(avg(scores)) : 0;
  });

  return result;
}

function computeTrendData(pitches: any[], meetings: any[], range: string): TrendPoint[] {
  const allSessions: { date: Date; score: number; aiScore: number; type: string }[] = [];

  const getSessionAIScore = (scoreObj: any, defaultScore: number) => {
    const categories = [
      "discovery",
      "pitch_delivery",
      "confidence_and_tone",
      "engagement",
      "closing",
      "opening",
    ];
    const catScores = categories
      .map((cat) => extractCategoryScore(scoreObj, cat))
      .filter((v) => v > 0);
    const skillAvg = catScores.length ? Math.round(avg(catScores)) : 0;
    return skillAvg > 0 ? skillAvg : defaultScore;
  };

  pitches.forEach((p) => {
    const score = p.overallPercentage || p.overallScore?.overall_percentange || 0;
    const date = new Date(p.createdAt || p.updatedAt);
    if (!isNaN(date.getTime()) && score > 0) {
      const s = p.overallScore?.overall_percentange !== undefined ? p.overallScore : p.categoryScores?.scores;
      const aiScore = getSessionAIScore(s, score);
      allSessions.push({ date, score, aiScore, type: "pitch" });
    }
  });

  meetings.forEach((m) => {
    const score = m.overallPercentage || m.ai_score?.overall_percentange || 0;
    const date = new Date(m.createdAt || m.updatedAt);
    if (!isNaN(date.getTime()) && score > 0) {
      const s = m.ai_score;
      const aiScore = getSessionAIScore(s, score);
      allSessions.push({ date, score, aiScore, type: "meeting" });
    }
  });

  if (!allSessions.length) return [];

  allSessions.sort((a, b) => a.date.getTime() - b.date.getTime());

  const useDaily = range === "7" || range === "15";

  const groupMap: Record<
    string,
    { scores: number[]; aiScores: number[]; pitchScores: number[]; practiceCount: number; date: Date }
  > = {};

  allSessions.forEach(({ date, score, aiScore, type }) => {
    let key: string;
    let groupDate: Date;

    if (useDaily) {
      groupDate = new Date(date);
      groupDate.setHours(0, 0, 0, 0);
      key = groupDate.toISOString().split("T")[0];
    } else {
      groupDate = new Date(date);
      groupDate.setDate(date.getDate() - date.getDay());
      groupDate.setHours(0, 0, 0, 0);
      key = groupDate.toISOString().split("T")[0];
    }

    if (!groupMap[key]) {
      groupMap[key] = { scores: [], aiScores: [], pitchScores: [], practiceCount: 0, date: groupDate };
    }
    groupMap[key].scores.push(score);
    groupMap[key].aiScores.push(aiScore);
    if (type === "pitch") {
      groupMap[key].pitchScores.push(score);
      groupMap[key].practiceCount++;
    }
  });

  return Object.entries(groupMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => ({
      label: v.date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      overall: Math.round(avg(v.scores)),
      skillAvg: Math.round(avg(v.pitchScores)) || Math.round(avg(v.scores)) - 5,
      aiScore: Math.round(avg(v.aiScores)),
      practice: v.practiceCount,
    }));
}

interface TrendPoint {
  label: string;
  overall: number;
  skillAvg: number;
  aiScore: number;
  practice: number;
}

/* ─── Collapsible Insight Section ──────────────────────────────────────── */
function CollapsibleInsight({
  label,
  color,
  icon,
  iconBg,
  text,
}: {
  label: string;
  color: string;
  icon: string;
  iconBg: string;
  text: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const { colors } = useDashboardTheme();

  return (
    <div
      style={{
        border: `1px solid ${color}28`,
        borderRadius: 12,
        padding: "12px 14px",
        background: `${color}06`,
      }}
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 14,
          }}
        >
          {icon}
        </div>
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            textTransform: "uppercase" as const,
            letterSpacing: "0.1em",
            color,
          }}
        >
          {label}
        </span>
      </div>

      {/* Text — clamp to 2 lines when collapsed */}
      <p
        style={{
          fontSize: 15,
          color: colors.text,
          margin: 0,
          lineHeight: 1.55,
          ...(expanded
            ? {}
            : {
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical" as any,
                overflow: "hidden",
              }),
        }}
      >
        {text}
      </p>

      {/* See more / See less toggle */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>

      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: "transparent",
          border: "none",
          color,
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          padding: "6px 0 0",
          display: "block",
          textAlign: "left",
        }}
      >
        {expanded ? "View less ↑" : "View more ↓"}
      </button>
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────── */

export default function RepDashboardPage({
  rep,
  userData,
  rawMeetings,
  onBack,
  meetingsLoading,
}: RepDashboardPageProps) {
  const { isDark, colors } = useDashboardTheme();
  
  const location = useLocation();
  const locState = (location.state as any) || {};
  const [tab, setTab] = useState<TabKey>(() => locState.repTab || locState.tab || "overview");

  useEffect(() => {
    const st = (location.state as any) || {};
    if (st.repTab || st.tab) {
      setTab(st.repTab || st.tab);
    }
  }, [location.state]);

  const [dateFilter, setDateFilter] = useState<string>("30");
  const [totalCatalogScenarios, setTotalCatalogScenarios] = useState<number>(0);
  
  useEffect(() => {
    scenarioService.getScenarios({ page: 1, limit: 1 })
      .then((res: { total: number }) => setTotalCatalogScenarios(res?.total || 0))
      .catch((err) => console.error("Error fetching scenarios count", err));
  }, []);

  const navigate = useNavigate();
  const setPageHeader = useUserStore((state) => state.setPageHeader);
  const setHideHeader = useUserStore((state) => state.setHideHeader);
  const repId = rep?.userId || rep?.id || rep?._id;
  const authTenantId = useAuthStore((state) => state.tenantid);
  const tenantId = authTenantId || rep?.tenantId || userData?.tenantId;
  const d = userData?.data || userData || {};
  const allUsers = d.tenantUserList?.items || [];
  const userDetail = (repId ? allUsers.find((u: any) => u.id === repId || u.userId === repId) : null) || {};
  
  const repMeetingsRaw: RawMeeting[] = (rawMeetings || []).filter(
    (m: any) =>
      m.userId === repId ||
      m.User?.id === repId ||
      m.user?.id === repId ||
      m.createdBy === repId
  );

  const { rawPitches, isLoading: pitchesLoading  } = usePitchDashboardData(true);
  const loading = meetingsLoading || pitchesLoading;

  const repPitchesRaw = useMemo(() => {
    return rawPitches.filter(
      (p: any) =>
        p.userId === repId ||
        p.user_id === repId ||
        p.user?.id === repId ||
        p.User?.id === repId ||
        p.createdBy === repId
    );
  }, [rawPitches, repId]);

  const filteredMeetings = useMemo(() => {
    return repMeetingsRaw.filter((m: any) => isWithinDateRange(m.createdAt || m.updatedAt, dateFilter));
  }, [repMeetingsRaw, dateFilter]);

  const filteredPitchesRaw = useMemo(() => {
    return repPitchesRaw.filter((p: any) => isWithinDateRange(p.createdAt || p.updatedAt, dateFilter));
  }, [repPitchesRaw, dateFilter]);

  const groups = useMemo(() => groupByUser(filteredMeetings), [filteredMeetings]);
  const meetingGroup = groups[0];

  const pitchGroups = useMemo(() => groupByUser(filteredPitchesRaw), [filteredPitchesRaw]);
  const pitchGroup = pitchGroups[0];

  useEffect(() => {
    setPageHeader("");
    setHideHeader(true);
    return () => {
      setPageHeader("Meeting Dashboard");
      setHideHeader(false);
    };
  }, [setPageHeader, setHideHeader]);

  const repPitches = useMemo(() => filteredPitchesRaw.filter((p: any) => !p.isScenario), [filteredPitchesRaw]);
  const repScenarios = useMemo(() => filteredPitchesRaw.filter((p: any) => p.isScenario), [filteredPitchesRaw]);
  const repPitchesAll = useMemo(() => repPitchesRaw.filter((p: any) => !p.isScenario), [repPitchesRaw]);
  const repScenariosAll = useMemo(() => repPitchesRaw.filter((p: any) => p.isScenario), [repPitchesRaw]);

  
  const pitchScores = useMemo(() => {
    return repPitches
      .map((p: any) => p.overallPercentage || p.score || 0)
      .filter((x: number) => x > 0);
  }, [repPitches]);

  const meetingScores = useMemo(() => {
    return filteredMeetings
      .map((m: any) => m.overallPercentage || m.score || 0)
      .filter((x: number) => x > 0);
  }, [filteredMeetings]);

  const scenarioScores = useMemo(() => {
    return repScenarios
      .map((s: any) => s.overallPercentage || s.score || s.overallScore || 0)
      .filter((x: number) => x > 0);
  }, [repScenarios]);

  const allScoresCombined = useMemo(() => [...pitchScores, ...meetingScores, ...scenarioScores], [pitchScores, meetingScores, scenarioScores]);
  const avgScore = useMemo(() => Math.round(avg(allScoresCombined)), [allScoresCombined]);
  const avgAIScore = useMemo(() => Math.round(avg([...pitchScores, ...scenarioScores])), [pitchScores, scenarioScores]);

  const totalMeetings = filteredMeetings.length;
  const totalPitches = repPitches.length;
  const totalScenarios = repScenarios.length;
  const totalPracticeSessions = totalPitches + totalScenarios;

  const completionRate = useMemo(() => {
    return totalCatalogScenarios > 0 
      ? Math.min(100, Math.round((totalScenarios / totalCatalogScenarios) * 100)) 
      : (totalScenarios > 0 ? 100 : 0);
  }, [totalScenarios, totalCatalogScenarios]);

  const skillScores = useMemo(
    () => computeSkillScoresFromPitches(repPitches, filteredMeetings),
    [repPitches, filteredMeetings]
  );

  const trendData = useMemo(
    () => computeTrendData([...repPitches, ...repScenarios], filteredMeetings, dateFilter),
    [repPitches, repScenarios, filteredMeetings, dateFilter]
  );

  const styles: any = {
    root: {
      minHeight: "100vh",
      background: colors.bg,
      color: colors.text,
      fontFamily: "'Inter', -apple-system, sans-serif",
      padding: "0",
    },
    topBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 32px",
      borderBottom: `1px solid ${colors.border}`,
      background: colors.surface,
    },
    backBtn: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 14px", color: colors.textMuted, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
    content: { padding: "24px 32px" },
    viewAll: {
      fontSize: 12, color: "#6C63FF", cursor: "pointer",
      fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
      border: `1px solid ${colors.border}`,
      borderRadius: 16, padding: "8px 12px",
    },
  };

  const handleBack = () => {
    if (tab !== "overview") {
      setTab("overview");
    } else {
      onBack();
    }
  };

  return (
    <div style={styles.root} className={isDark ? "" : "light-theme-dashboard"}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .rep-card:hover { background: rgba(255,255,255,0.06) !important; }
        .activity-row:hover { background: rgba(255,255,255,0.04) !important; }
        .insight-card:hover { border-color: rgba(124,112,232,0.5) !important; }
        .action-card:hover { border-color: rgba(124,112,232,0.4) !important; background: rgba(124,112,232,0.08) !important; }
      `}</style>

      {/* Top Bar */}
    

      <div style={styles.content}>
        {/* Profile Row */}
        <div style={{ display: "flex", alignItems: "center", gap: 18, paddingBottom: 20, marginBottom: 4 }}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: avatarColor(rep?.name || "U"),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 26,
              fontWeight: 700,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            {getInitials(rep?.name || "U")}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: "#fff", margin: 0 }}>
                {rep?.name || "Unknown Rep"}
              </h1>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "3px 12px",
                  borderRadius: 999,
                  background: "rgba(74,222,128,0.12)",
                  color: "#4ADE80",
                  border: "1px solid rgba(74,222,128,0.28)",
                }}
              >
                Active
              </span>
            </div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: 0 }}>
              {userDetail?.role
                ? userDetail.role.charAt(0) + userDetail.role.slice(1).toLowerCase().replace("_", " ")
                : "Sales Rep"}{" "}
              · Joined {fmtDate(userDetail?.createdAt || rep?.createdAt)}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
           
            <button style={styles.backBtn} onClick={handleBack}>
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            justifyContent:"space-between",
            gap: 0,
            marginBottom: 28,
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: "11px 24px",
                fontSize: 16,
                fontWeight: 600,
                cursor: "pointer",
                background: "transparent",
                border: "none",
                color: tab === t.key ? "#fff" : "rgba(255,255,255,0.45)",
                borderBottom: tab === t.key ? "2px solid #7C70E8" : "2px solid transparent",
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          ))}
          </div>
          <div>
           <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                padding: "8px 14px",
                color: "#fff",
                fontSize: 16,
                outline: "none",
                cursor: "pointer",
                fontFamily: "'Inter', -apple-system, sans-serif",
              }}
            >
              <option value="7" style={{ background: "#0d0d1a", color: "#fff" }}>Last 7 Days</option>
              <option value="15" style={{ background: "#0d0d1a", color: "#fff" }}>Last 15 Days</option>
              <option value="30" style={{ background: "#0d0d1a", color: "#fff" }}>Last 30 Days</option>
              <option value="60" style={{ background: "#0d0d1a", color: "#fff" }}>Last 2 Months</option>
              <option value="all" style={{ background: "#0d0d1a", color: "#fff" }}>All time</option>
            </select>
            </div>
        </div>

        {/* ── Content Area ── */}
        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "rgba(255,255,255,0.4)", fontSize: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 12, display: 'inline-block', animation: "spin 1s linear infinite" }}>⟳</div><br/>Loading dashboard data...
          </div>
        ) : (
          <>
            {tab === "overview" && (
              <OverviewTab
                rep={rep}
                avgScore={avgScore}
                avgAIScore={avgAIScore}
                totalMeetings={totalMeetings}
                totalPitches={totalPitches}
                totalScenarios={totalScenarios}
                totalPracticeSessions={totalPracticeSessions}
                skillScores={skillScores}
                repPitches={repPitches}
                repScenarios={repScenarios}
                repPitchesAll={repPitchesAll}
                repScenariosAll={repScenariosAll}
                repMeetings={filteredMeetings}
                repMeetingsRaw={repMeetingsRaw}
                trendData={trendData}
                onSwitchToPitches={() => setTab("pitches")}
                onSwitchToMeetings={() => setTab("meetings")}
                onSwitchToScripts={() => setTab("scripts")}
                navigate={navigate}
                location={location}
                dateFilter={dateFilter}
                completionRate={completionRate}
                tenantId={tenantId}
              />
            )}
            {tab === "meetings" && (
              <div>
                {meetingGroup ? (
                  <MeetingCardMain group={meetingGroup} mode="meeting" />
                ) : (
                  <EmptyState message="No meetings recorded yet for this rep." />
                )}
              </div>
            )}
            {tab === "pitches" && (
              <div>
                {pitchGroup ? (
                  <PitchCardMain group={pitchGroup} />
                ) : (
                  <EmptyState message="No pitches recorded yet for this rep." />
                )}
              </div>
            )}
            {tab === "scripts" && (
              <div>
                <RepScriptsTab rep={rep} tenantId={tenantId} dateFilter={dateFilter} />
              </div>
            )}
          </>
        )}
      </div>

    </div>
    
  );
}

/* ─── Overview Tab ──────────────────────────────────────────────────────── */

interface OverviewTabProps {
  rep: any;
  avgScore: number;
  avgAIScore: number;
  totalMeetings: number;
  totalPitches: number;
  totalScenarios: number;
  totalPracticeSessions: number;
  skillScores: Record<string, number>;
  repPitches: any[];
  repScenarios: any[];
  repPitchesAll: any[];
  repScenariosAll: any[];
  repMeetings: any[];
  repMeetingsRaw: any[];
  trendData: TrendPoint[];
  onSwitchToPitches: () => void;
  onSwitchToMeetings: () => void;
  onSwitchToScripts?: () => void;
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  dateFilter: string;
  completionRate: number;
  tenantId?: string;
}

function OverviewTab(props: OverviewTabProps) {

  const {
    rep,
    avgScore,
    avgAIScore,
    totalMeetings,
    totalPracticeSessions,
    skillScores,
    repPitches,
    repScenarios,
    repPitchesAll,
    repScenariosAll,
    repMeetings,
    repMeetingsRaw,
    trendData,
    onSwitchToPitches,
    onSwitchToMeetings,
    onSwitchToScripts,
    navigate,
    location,
    dateFilter,
    tenantId,
  } = props;

  const { isDark, colors } = useDashboardTheme();

  const repId = rep?.userId;
  const { data: scriptDashboardRes } = useScriptDashboardOverview(tenantId || "");
  const scriptActivities = scriptDashboardRes?.data?.recentPracticeActivities || [];
  const repScriptActivities = useMemo(() => {
    if (!repId) return scriptActivities;
    return scriptActivities.filter((act: any) => act.repId === repId);
  }, [scriptActivities, repId]);

  const scriptsSummary = scriptDashboardRes?.data?.summary || {
    totalCompletedAssignments: 0,
    totalAssignments: 0,
    completionPercentage: 0,
  };
  const scriptsCompletedCount = scriptsSummary.totalCompletedAssignments || 0;



 function InsightsRequirementNotice({
  pitchCount,
  practiceCount,
  validMeetingCount,
  validRecordingCount,
}: {
  pitchCount: number;
  practiceCount: number;
  validMeetingCount: number;
  validRecordingCount: number;
}) {
  const meetsPitchPractice = pitchCount >= 1 && practiceCount >= 1;
  const meetsMeetingsRecordings = validMeetingCount >= 1 || validRecordingCount >= 1;

  return (
    <div style={{
      background: "rgba(250,204,21,0.06)", border: "1px solid rgba(250,204,21,0.25)",
      borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column" as const, gap: 12,
      width: "100%",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 18 }}>🔒</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#FACC15", textTransform: "uppercase" as const, letterSpacing: 0.5 }}>
          Insufficient Data For Pitchster Insights
        </span>
      </div>
      <p style={{ fontSize: 14, color: "rgba(255,255,255,0.7)", margin: 0, lineHeight: 1.5 }}>
        To unlock insights, complete at least <strong style={{ color: "#fff" }}>1 scored pitch</strong> and <strong style={{ color: "#fff" }}>1 scored practice session</strong>, OR upload at least <strong style={{ color: "#fff" }}>1 scored meeting or recording</strong> in the last 30 days.
      </p>
      
      <div style={{ display: "flex", flexDirection: "column" as const, gap: 10, marginTop: 4 }}>
        {/* Path A: Pitch + Practice */}
        <div style={{
          background: meetsPitchPractice ? "rgba(74,222,128,0.04)" : "rgba(255,255,255,0.02)",
          border: `1px solid ${meetsPitchPractice ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.05)"}`,
          borderRadius: 8, padding: "10px 12px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: meetsPitchPractice ? "#4ADE80" : "rgba(255,255,255,0.5)" }}>
              Path A: Pitch + Practice {meetsPitchPractice ? "✓ (Completed)" : ""}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 16, height: 16, borderRadius: "50%",
                background: pitchCount >= 1 ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.06)",
                border: `1px solid ${pitchCount >= 1 ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.15)"}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#4ADE80"
              }}>{pitchCount >= 1 ? "✓" : ""}</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Scored Pitch(es)</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>{pitchCount}/1</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 16, height: 16, borderRadius: "50%",
                background: practiceCount >= 1 ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.06)",
                border: `1px solid ${practiceCount >= 1 ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.15)"}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#4ADE80"
              }}>{practiceCount >= 1 ? "✓" : ""}</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Scored Practice Session(s)</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>{practiceCount}/1</span>
            </div>
          </div>
        </div>

        {/* Path B: Meeting or Recording */}
        <div style={{
          background: meetsMeetingsRecordings ? "rgba(74,222,128,0.04)" : "rgba(255,255,255,0.02)",
          border: `1px solid ${meetsMeetingsRecordings ? "rgba(74,222,128,0.2)" : "rgba(255,255,255,0.05)"}`,
          borderRadius: 8, padding: "10px 12px"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: meetsMeetingsRecordings ? "#4ADE80" : "rgba(255,255,255,0.5)" }}>
              Path B: Meeting or Recording {meetsMeetingsRecordings ? "✓ (Completed)" : ""}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column" as const, gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 16, height: 16, borderRadius: "50%",
                background: validMeetingCount >= 1 ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.06)",
                border: `1px solid ${validMeetingCount >= 1 ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.15)"}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#4ADE80"
              }}>{validMeetingCount >= 1 ? "✓" : ""}</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Scored Meeting(s)</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>{validMeetingCount}/1</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{
                width: 16, height: 16, borderRadius: "50%",
                background: validRecordingCount >= 1 ? "rgba(74,222,128,0.15)" : "rgba(255,255,255,0.06)",
                border: `1px solid ${validRecordingCount >= 1 ? "rgba(74,222,128,0.4)" : "rgba(255,255,255,0.15)"}`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#4ADE80"
              }}>{validRecordingCount >= 1 ? "✓" : ""}</span>
              <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>Scored Recording(s)</span>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>{validRecordingCount}/1</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


  const [coachLoading, setCoachLoading] = useState(false);
  const [coachError, setCoachError] = useState<string | null>(null);
  const [coachData, setCoachData] = useState<any | null>(null);
const [coachOpen, setCoachOpen] = useState(false);
const [actionModalData, setActionModalData] = useState<any | null>(null); 

  const [coachSkipMessage, setCoachSkipMessage] = useState<string | null>(null);
  const [isAssessmentExpanded, setIsAssessmentExpanded] = useState(false);
  const [isMeetingAnalysisExpanded, setIsMeetingAnalysisExpanded] = useState(false);
  const eligibility = useMemo(
    () => evaluateRepInsightsEligibility({ repPitches: repPitchesAll, repScenarios: repScenariosAll, repMeetings: repMeetingsRaw, coachData }),
    [repPitchesAll, repScenariosAll, repMeetingsRaw, coachData]
  );
  const [insightsFetched, setInsightsFetched] = useState(false);

  // Auto-fetch insights on mount
useEffect(() => {
  if (insightsFetched) return;
  const uid = rep?.userId;
  if (!uid) return;
  setInsightsFetched(true);
  setCoachLoading(true);
  getRepCoachingInsights(uid)
    .then((res: any) => {
      const data = res?.status !== undefined ? res : (res?.data ?? res);
      setCoachData(data);
      setCoachLoading(false);
    })
    .catch((err: any) => {
      setCoachLoading(false);
      setCoachError(err?.response?.data?.message || err?.message || "Failed to load insights.");
    });
}, [rep?.userId]);


 const generateInsights = async () => {
  const uid = rep?.userId;
  if (!uid) return;

  setCoachError(null);
  setCoachSkipMessage(null);
  setCoachLoading(true);

  try {
    const genRes = await generateRepCoachingInsights(uid); // POST — ab synchronous
    const genData = genRes?.status !== undefined ? genRes : (genRes?.data ?? genRes);

    if (genData?.skippedGeneration) {
      setCoachData(genData);
      setCoachSkipMessage(genData.message);
      setCoachLoading(false);
      return;
    }

    setCoachData(genData);
    setCoachLoading(false);
  } catch (err: any) {
    setCoachLoading(false);
    setCoachError(err?.response?.data?.message || err?.message || "Failed.");
  }
};

  const insights = coachData?.insights;

  const formattedPitchAnalysis = insights?.pitchAnalysis || insights?.overallAssessment || "";
  const formattedMeetingAnalysis = insights?.meetingAnalysis || "";

  // Skill entries
  const skillEntries = Object.entries(skillScores).map(([k, v]) => {
    let label = k
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    if (k === "confidence_and_tone") {
      label = "Confidence & Tone";
    }
    return {
      key: k,
      label,
      score: v,
    };
  });

  const priorityFocusAreas = insights?.priorityFocusAreas;
  const weakKeys = Array.isArray(priorityFocusAreas)
    ? priorityFocusAreas.map((item: any) => item.area?.toLowerCase()?.trim()).filter(Boolean)
    : [];

  let strengths: any[] = [];
  let weaknesses: any[] = [];

  if (weakKeys.length > 0) {
    weaknesses = skillEntries.filter((s) => weakKeys.includes(s.key.toLowerCase()));
    strengths = skillEntries.filter((s) => !weakKeys.includes(s.key.toLowerCase()));
    strengths.sort((a, b) => b.score - a.score);
    weaknesses.sort((a, b) => b.score - a.score);
  } else {
    const sorted = [...skillEntries].sort((a, b) => b.score - a.score);
    strengths = sorted.filter((s) => s.score >= 60).slice(0, 3);
    weaknesses = sorted.filter((s) => s.score < 60).slice(0, 3);
  }



  function getScore(item: any) {
  return Number(
    item?.overallPercentage ??
    item?.overallScore?.overall_percentange ??
    item?.ai_score?.overall_percentange ??
    item?.overallScore ??
    item?.score ??
    0
  );
}


function capitalize(value: any) {
  const text = String(value || "").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
}
function getActivityDetail(item: any, type: string) {
  if (type === "Role Play") {
    return (
      item?.scenario?.title ||
      item?.scenarioTitle ||
      item?.title ||
      item?.name ||
      "Role Play"
    );
  }

  if (type === "Meeting Uploaded") {
    const meetingType = capitalize(item?.otherDetails?.meetingType || item?.meetingType);
    return (
      meetingType ||
      item?.TargetCompanyProfile?.companyName ||
      item?.customerData?.companyName ||
      item?.companyName ||
      item?.title ||
      "Meeting"
    );
  }

  return (
    item?.pitchTitle ||
    capitalize(item?.otherDetails?.meetingType) ||
    item?.TargetCompanyProfile?.companyName ||
    item?.customerData?.companyName ||
    item?.title ||
    item?.name ||
    "Pitch"
  );
}

  const [activityPage, setActivityPage] = useState(1);

  useEffect(() => {
    setActivityPage(1);
  }, [dateFilter]);

  // Recent activity
  const recentActivity = [
    ...repScenarios.map((s: any) => ({
      id: s.id || s._id || s.legacyMongoId || s.meetingId || s.sessionId || '',
      raw: s,
      type: "Role Play",
      typeColor: "#7C70E8",
      detail: getActivityDetail(s, "Role Play"),
      score: getScore(s),
      date: s.createdAt,
    })),

    ...repMeetings.map((m: any) => ({
      id: m.id || m._id || m.legacyMongoId || m.meetingId || m.sessionId || '',
      raw: m,
      type: "Meeting Uploaded",
      typeColor: "#22B4C8",
      detail: getActivityDetail(m, "Meeting Uploaded"),
      score: getScore(m),
      date: m.createdAt,
    })),

    ...repPitches.map((p: any) => {
      const isDigitalTwin = /digital\s*twin/i.test(p.pitchTitle || "");
      const type = isDigitalTwin ? "Digital Twin" : "Pitch";
      return {
        id: p.id || p._id || p.legacyMongoId || p.meetingId || p.sessionId || '',
        raw: p,
        type,
        typeColor: isDigitalTwin ? "#A78BFA" : "#F59E0B",
        detail: getActivityDetail(p, type),
        score: getScore(p),
        date: p.createdAt,
      };
    }),

    ...repScriptActivities.map((sa: any) => {
      const completedSec = (sa.sections || []).filter((sec: any) => sec.isCompleted).length;
      const totalSec = (sa.sections || []).length || 4;
      const score = Math.round((completedSec / totalSec) * 100);
      return {
        id: sa.assignmentId || sa.scriptMetaId,
        raw: sa,
        type: "Script Activity",
        typeColor: "#4ADE80",
        detail: sa.scriptTitle || "Script Practice",
        score: score,
        date: sa.createdAt,
        isScript: true,
      };
    }),
  ].filter((item: any) => (item.score || 0) > 0).sort(
    (a, b) =>
      new Date(b.date).getTime() -
      new Date(a.date).getTime()
  );

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(recentActivity.length / itemsPerPage));
  const startIndex = (activityPage - 1) * itemsPerPage;
  const paginatedActivities = recentActivity.slice(startIndex, startIndex + itemsPerPage);


  // ── Coaching insight sections (from API or fallback static) ──
  const quickWinText =
    insights?.quickWin ||
    "End every pitch by directly asking for the next step and summarizing key benefits to reinforce urgency.";
  const managerTipText =
    insights?.managerTip ||
    "Role-play closing scenarios in the next 1:1, focusing on asking for next steps and summarizing key points, using real objections from Proposal Presentation.";
  const objectionText =
    insights?.objectionCoaching ||
    "For 'What's the peer-reviewed data behind this?', prepare a concise summary of relevant studies or industry benchmarks. If unavailable, confidently acknowledge the gap and redirect to real-world results or testimonials, emphasizing transparency and value.";

  // Recommended actions from API
  const recommendedActions = (insights?.priorityFocusAreas || []).slice(0, 3).map((fa: any, i: number) => ({
    icon: ["🎭", "💬", "👥"][i] || "⭐",
    iconBg: ["rgba(124,112,232,0.15)", "rgba(251,146,60,0.12)", "rgba(34,197,94,0.12)"][i],
    color: ["#7C70E8", "#FB923C", "#34D399"][i],
    title: fa.area?.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
    area: fa.area,
    why: fa.why,
    specificAction: fa.specificAction,
    evidence: fa.evidence,
    recommendedScenario: fa.recommendedScenario,
  }));

 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* ── Stat Cards Row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr 1fr 1fr 1fr", gap: 14 }}>
        {/* Overall Readiness Score */}
        <div
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.topCardBorder}`,
            borderRadius: 14,
            padding: "18px 20px",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          <CircularScore score={avgScore} />
          <div>
            <div style={{ fontSize: 16, color: colors.text, marginBottom: 4, lineHeight: 1.3 }}>
              Overall Readiness Score
            </div>
            {/* <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: scoreColor(avgScore),
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>↑ Excellent</span>
            </div>
            <div style={{ fontSize: 13, color: "#fff", marginTop: 2 }}>
              vs last 30 days
            </div> */}
          </div>
        </div>

        <StatCard
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#7C70E8" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          }
          iconBg="rgba(124,112,232,0.15)"
          label="Practice Sessions"
          value={totalPracticeSessions}
          sub={`in ${dateFilter === "all" ? "all time" : dateFilter === "60" ? "last 2 months" : `last ${dateFilter} days`}`}
          onViewAll={onSwitchToPitches}
        />

        <StatCard
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22B4C8" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
          }
          iconBg="rgba(34,180,200,0.15)"
          label="Meetings Analyzed"
          value={totalMeetings}
          sub={`in ${dateFilter === "all" ? "all time" : dateFilter === "60" ? "last 2 months" : `last ${dateFilter} days`}`}
          onViewAll={onSwitchToMeetings}
        />

        <StatCard
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="1.8"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
          }
          iconBg="rgba(74,222,128,0.12)"
          label="Scripts Completed"
          value={scriptsCompletedCount}
          sub={`in ${dateFilter === "all" ? "all time" : dateFilter === "60" ? "last 2 months" : `last ${dateFilter} days`}`}
          onViewAll={onSwitchToScripts}
        />

        <StatCard
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#A78BFA" strokeWidth="1.8"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
          }
          iconBg="rgba(167,139,250,0.15)"
          label="Pitch Score (Avg)"
          value={avgAIScore > 0 ? `${avgAIScore}%` : "—"}
          sub={`avg for ${dateFilter === "all" ? "all time" : dateFilter === "60" ? "last 2 months" : `last ${dateFilter} days`}`}
          valueBig
        />
      </div>

      {/* ── Middle Row ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        {/* Skill Performance */}
        <div
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "20px 22px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3 ,}}>
              SKILL PERFORMANCE
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.textMuted} strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {skillEntries.map(({ key, label, score }) => (
              <SkillBar key={key} label={label} value={score} icon={skillIconForKey(key)} />
            ))}
          </div>
        </div>
 {coachData?.status === "not_generated" ? (
    <div
      style={{
        gridColumn: "span 2",
        background: colors.topCardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: "32px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 44, height: 44, borderRadius: 14,
          background: "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))",
          border: "1px solid rgba(184,248,144,0.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth="1.8">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>
        AI Coaching Insights Ready to Generate
      </div>
      <p style={{ fontSize: 13, color: colors.textMuted, margin: 0, maxWidth: 320 }}>
        Generate personalized coaching insights based on this rep's recent pitches and role-play sessions.
      </p>
      <button
        onClick={generateInsights}
        // disabled={coachLoading}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700,
          background: coachLoading
            ? (isDark ? "linear-gradient(135deg, rgba(120,119,198,0.2), rgba(120,119,198,0.08))" : "linear-gradient(135deg, rgba(120,119,198,0.1), rgba(120,119,198,0.05))")
            : (isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))"),
          color: coachLoading ? colors.textMuted : (isDark ? "#B8F890" : "#15803D"),
          border: coachLoading 
            ? `1px solid ${colors.border}` 
            : (isDark ? "1px solid rgba(184,248,144,0.3)" : "1px solid rgba(34,197,94,0.3)"),
          cursor: coachLoading ? "not-allowed" : "pointer",
          transition: "all 0.2s",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
        {coachLoading ? "Generating..." : "Generate Insights"}
      </button>
    </div>
  ) : coachData?.status === "insufficient_data" ? (
    <div
      style={{
        gridColumn: "span 2",
        background: colors.topCardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: 16,
        padding: "20px 22px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 480 }}>
        <InsightsRequirementNotice
          pitchCount={coachData?.dataSufficiency?.pitchCount ?? eligibility.pitchScoreCount}
          practiceCount={coachData?.dataSufficiency?.practiceCount ?? eligibility.scenarioScoreCount}
          validMeetingCount={coachData?.dataSufficiency?.validMeetingCount ?? eligibility.validMeetingCount}
          validRecordingCount={coachData?.dataSufficiency?.validRecordingCount ?? eligibility.validRecordingCount}
        />
      </div>
    </div>
  )  : coachData?.status === "failed" ? (
  <div style={{ 
    gridColumn: "span 2", 
    background: isDark ? "rgba(248,113,113,0.05)" : "rgba(239,68,68,0.05)", 
    border: isDark ? "1px solid rgba(248,113,113,0.2)" : "1px solid rgba(239,68,68,0.2)", 
    borderRadius: 16, padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" 
  }}>
    <div style={{ fontSize: 15, fontWeight: 600, color: colors.text }}>Insight generation failed</div>
    <p style={{ fontSize: 13, color: colors.textMuted, margin: 0 }}>{coachData?.message || "Please try again."}</p>
    <button 
      onClick={generateInsights} 
      disabled={coachLoading} 
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700,
        background: coachLoading
          ? (isDark ? "linear-gradient(135deg, rgba(120,119,198,0.2), rgba(120,119,198,0.08))" : "linear-gradient(135deg, rgba(120,119,198,0.1), rgba(120,119,198,0.05))")
          : (isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))"),
        color: coachLoading ? colors.textMuted : (isDark ? "#B8F890" : "#15803D"),
        border: coachLoading 
          ? `1px solid ${colors.border}` 
          : (isDark ? "1px solid rgba(184,248,144,0.3)" : "1px solid rgba(34,197,94,0.3)"),
        cursor: coachLoading ? "not-allowed" : "pointer",
        transition: "all 0.2s",
      }}
    >
      {coachLoading ? "Generating..." : "Try Again"}
    </button>
  </div>
):(<>
        {!coachData?.insights?.insufficientData && (
          <div
            style={{
              background: colors.topCardBg,
              border: `1px solid ${colors.border}`,
              borderRadius: 16,
              padding: "20px 22px",
            }}
          >

          {(formattedPitchAnalysis || formattedMeetingAnalysis) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 12 }}>
              {formattedPitchAnalysis && (
                <div
                  style={{
                    background: isDark ? "rgba(167,139,250,0.07)" : "rgba(124,112,232,0.05)",
                    border: isDark ? "1px solid rgba(167,139,250,0.25)" : "1px solid rgba(124,112,232,0.2)",
                    borderRadius: 14,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: isDark ? "#a78bfa" : "#6366F1",
                        fontWeight: 700,
                      }}
                    >
                      Pitch Analysis
                    </span>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      color: colors.text,
                      fontSize: 15,
                      lineHeight: 1.8,
                      display: isAssessmentExpanded ? "block" : "-webkit-box",
                      WebkitLineClamp: isAssessmentExpanded ? undefined : 1,
                      WebkitBoxOrient: "vertical" as const,
                      overflow: isAssessmentExpanded ? "visible" : "hidden",
                    }}
                  >
                    {formattedPitchAnalysis}
                  </p>

                  <button
                    onClick={() => setIsAssessmentExpanded(!isAssessmentExpanded)}
                    style={{
                      background: "none",
                      border: "none",
                      color: isDark ? "#a78bfa" : "#6366F1",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "4px 0",
                      marginTop: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = isDark ? "#c084fc" : "#4f46e5")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = isDark ? "#a78bfa" : "#6366F1")}
                  >
                    {isAssessmentExpanded ? "View Less" : "View More"}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      style={{
                        transform: isAssessmentExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
              )}

              {formattedMeetingAnalysis && (
                <div
                  style={{
                    background: "rgba(167,139,250,0.07)",
                    border: "1px solid rgba(167,139,250,0.25)",
                    borderRadius: 14,
                    padding: "12px 14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#a78bfa",
                        fontWeight: 700,
                      }}
                    >
                      Meeting Analysis
                    </span>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      color: "#fff",
                      fontSize: 15,
                      lineHeight: 1.8,
                      display: isMeetingAnalysisExpanded ? "block" : "-webkit-box",
                      WebkitLineClamp: isMeetingAnalysisExpanded ? undefined : 1,
                      WebkitBoxOrient: "vertical" as const,
                      overflow: isMeetingAnalysisExpanded ? "visible" : "hidden",
                    }}
                  >
                    {formattedMeetingAnalysis}
                  </p>

                  <button
                    onClick={() => setIsMeetingAnalysisExpanded(!isMeetingAnalysisExpanded)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#a78bfa",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      padding: "4px 0",
                      marginTop: 8,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#c084fc")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#a78bfa")}
                  >
                    {isMeetingAnalysisExpanded ? "View Less" : "View More"}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      style={{
                        transform: isMeetingAnalysisExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : coachLoading ? (
            <div style={{ fontSize: 13, color: colors.textMuted, fontStyle: "italic", marginBottom: 12 }}>
              Loading analysis…
            </div>
          ) : coachData?.insights?.insufficientData ? (
            <div></div>
          ) : (
            <div style={{ fontSize: 13, color: colors.textMuted, fontStyle: "italic", marginBottom: 12 }}>
              Generate insights to see the analysis.
            </div>
          )}
         
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div  style={{
            background: colors.surfaceAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "18px 20px",
          }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3, marginBottom: 12 }}>
                ✅ STRENGTHS
              </div>
              {strengths.length > 0 ? (
                strengths.map((s) => (
                  <div
                    key={s.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      fontSize: 13,
                      color: colors.text,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                    {s.label}
                  </div>
                ))
              ) : (
                ["Opening & Attention", "Confidence & Tone", "Professionalism"].map((s) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13, color: colors.text }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                    {s}
                  </div>
                ))

              )}
            </div>

            <div  style={{
            background: colors.surfaceAlt,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "18px 20px",
          }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#FACC15" }}>⚠</span> AREAS TO IMPROVE
              </div>
              {weaknesses.length > 0 ? (
                weaknesses.map((s) => (
                  <div
                    key={s.key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      fontSize: 13,
                      color: colors.text,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                    {s.label}
                  </div>
                ))
              ) : (
                ["Closing & Urgency", "Discovery Questions", "Objection Handling"].map((s) => (
                  <div key={s} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 13, color: colors.text }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                    {s}
                  </div>
                ))
              )}
            </div>
          </div>
          </div>
        )}

        {/* ── Recent Coaching Insights — 3 collapsible sections ── */}
        <div
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "18px 20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 , justifyContent:"space-between"}}>
            <div style={{ display: "flex", alignItems: "center", gap: 8,}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isDark ? "#A78BFA" : "#6366F1"} strokeWidth="1.8"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3 }}>
              RECENT COACHING INSIGHTS
            </div>
            </div>

{eligibility.shouldShowGenerateButton && (
  <button
    onClick={generateInsights}
    disabled={coachLoading}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 16px",
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 700,
      background: coachLoading ? (isDark ? "linear-gradient(135deg, rgba(120,119,198,0.2), rgba(120,119,198,0.08))" : "linear-gradient(135deg, rgba(120,119,198,0.1), rgba(120,119,198,0.05))") : (isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))"),
      color: coachLoading ? colors.textMuted : (isDark ? "#B8F890" : "#15803D"),
      border: coachLoading ? `1px solid ${colors.border}` : (isDark ? "1px solid rgba(184,248,144,0.3)" : "1px solid rgba(34,197,94,0.3)"),
      cursor: coachLoading ? "not-allowed" : "pointer",
      marginBottom: 12 ,
      width: "fit-content",
      transition: "all 0.2s",
      opacity: coachLoading ? 0.8 : 1,
    }}
    onMouseEnter={(e) => {
      if (!coachLoading) {
        e.currentTarget.style.background = isDark ? "linear-gradient(135deg, rgba(184,248,144,0.25), rgba(184,248,144,0.1))" : "linear-gradient(135deg, rgba(34,197,94,0.25), rgba(34,197,94,0.1))";
        e.currentTarget.style.borderColor = isDark ? "rgba(184,248,144,0.5)" : "rgba(34,197,94,0.5)";
      }
    }}
    onMouseLeave={(e) => {
      if (!coachLoading) {
        e.currentTarget.style.background = isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))";
        e.currentTarget.style.borderColor = isDark ? "rgba(184,248,144,0.3)" : "rgba(34,197,94,0.3)";
      }
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
    Generate Insights
  </button>
)}

{coachData?.status === "done" && (  <button
    onClick={generateInsights}
    disabled={coachLoading}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 16px",
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 700,
      background: coachLoading ? (isDark ? "linear-gradient(135deg, rgba(120,119,198,0.2), rgba(120,119,198,0.08))" : "linear-gradient(135deg, rgba(120,119,198,0.1), rgba(120,119,198,0.05))") : (isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))"),
      color: coachLoading ? colors.textMuted : (isDark ? "#B8F890" : "#15803D"),
      border: coachLoading ? `1px solid ${colors.border}` : (isDark ? "1px solid rgba(184,248,144,0.3)" : "1px solid rgba(34,197,94,0.3)"),
      cursor: coachLoading ? "not-allowed" : "pointer",
      marginBottom: 12 ,
      width: "fit-content",
      transition: "all 0.2s",
      opacity: coachLoading ? 0.8 : 1,
    }}
    onMouseEnter={(e) => {
      if (!coachLoading) {
        e.currentTarget.style.background = isDark ? "linear-gradient(135deg, rgba(184,248,144,0.25), rgba(184,248,144,0.1))" : "linear-gradient(135deg, rgba(34,197,94,0.25), rgba(34,197,94,0.1))";
        e.currentTarget.style.borderColor = isDark ? "rgba(184,248,144,0.5)" : "rgba(34,197,94,0.5)";
      }
    }}
    onMouseLeave={(e) => {
      if (!coachLoading) {
        e.currentTarget.style.background = isDark ? "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))";
        e.currentTarget.style.borderColor = isDark ? "rgba(184,248,144,0.3)" : "rgba(34,197,94,0.3)";
      }
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
    Regenerate Insights
  </button>
)}

{coachSkipMessage && (
  <div style={{
    fontSize: 12,
    color: isDark ? "rgba(184,248,144,0.7)" : "#15803D",
    marginTop: 8,
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }}>
    ℹ️ {coachSkipMessage}
  </div>
)}
          </div>

          {coachLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0" }}>
              <div style={{ width: 22, height: 22, borderRadius: "50%", border: "2px solid rgba(167,139,250,0.2)", borderTopColor: "#A78BFA", animation: "spin 1s linear infinite" }} />
            </div>
          ) : coachData?.insights?.insufficientData ? (
              <InsightsRequirementNotice
                pitchCount={coachData?.dataSufficiency?.pitchCount ?? eligibility.pitchScoreCount}
                practiceCount={coachData?.dataSufficiency?.practiceCount ?? eligibility.scenarioScoreCount}
                validMeetingCount={coachData?.dataSufficiency?.validMeetingCount ?? eligibility.validMeetingCount}
                validRecordingCount={coachData?.dataSufficiency?.validRecordingCount ?? eligibility.validRecordingCount}
              />          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {/* existing CollapsibleInsight x3 — yeh cached insight tab tak dikhte rahenge jab tak naya session add na ho */}
              <CollapsibleInsight label="Quick Win" color="#34d399" icon="⚡" iconBg="rgba(52,211,153,0.15)" text={quickWinText} />
              <CollapsibleInsight label="Manager Tip" color="#38bdf8" icon="💡" iconBg="rgba(56,189,248,0.15)" text={managerTipText} />
              <CollapsibleInsight label="Objection Coaching" color="#f59e0b" icon="🛡️" iconBg="rgba(245,158,11,0.15)" text={objectionText} />
            </div>
          )}
        </div>
      </>
    )}
  </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Recent Activity */}
        <div
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "20px 22px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3 }}>
              RECENT ACTIVITY {dateFilter !== "30" && `(${dateFilter === "all" ? "ALL TIME" : dateFilter === "60" ? "LAST 2 MONTHS" : `LAST ${dateFilter} DAYS`})`}
            </div>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "160px 220px 140px 130px 74px", gap: 12, padding: "0 6px 10px", borderBottom: `1px solid ${colors.border}` }}>
            {["Activity", "Details", "Pitch Score", "Date", ""].map((h) => (
              <span
                key={h}
                style={{
                  fontSize: 13,
                  color: colors.text,
                  fontWeight: 600,
                  textTransform: "uppercase" as const,
                  letterSpacing: 0.5,
                }}
              >
                {h}
              </span>
            ))}
          </div>
          <div style={{ height:"260px" , overflowY:"auto"}}>
            <div style={{ flex: 1 }}>
              {paginatedActivities.length > 0 ? (
                paginatedActivities.map((a, i) => (
                  <div
                    key={i}
                    className="activity-row"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "160px 220px 140px 130px 74px",
                      gap: 12,
                      padding: "12px 6px",
                      borderBottom: `1px solid ${colors.divider}`,
                      alignItems: "center",
                      transition: "background 0.15s",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if ((a as any).isScript) {
                        navigate(`/script-details/${a.id}?assignedToId=${a.raw?.repId || repId}`, { state: { from: location.pathname + location.search, selectedRep: rep, repTab: "overview" } });
                      } else if (a.id) {
                        navigate(`/meeting-detail/${a.id}`, { state: { meeting: a.raw, meetingId: a.id, from: location.pathname + location.search, selectedRep: rep, repTab: "overview" } });
                      }
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = colors.rowHover)}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <div>
                      <Badge type={a.type} />
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: colors.text,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap" as const,
                      }}
                    >
                      {a.detail}
                    </div>
                    <div>
                      {a.score > 0 ? (
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            padding: "3px 9px",
                            borderRadius: 20,
                            background: a.score >= 70 ? "rgba(74,222,128,0.1)" : a.score >= 50 ? "rgba(250,204,21,0.1)" : "rgba(248,113,113,0.1)",
                            color: a.score >= 70 ? "#4ADE80" : a.score >= 50 ? "#FACC15" : "#F87171",
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {a.score}%
                        </span>
                      ) : (
                        <span  style={{
                            fontSize: 13,
                            fontWeight: 700,
                            padding: "3px 9px",
                            borderRadius: 20,
                            background: a.score >= 70 ? "rgba(74,222,128,0.1)" : a.score >= 50 ? "rgba(250,204,21,0.1)" : "rgba(248,113,113,0.1)",
                            color: a.score >= 70 ? "#4ADE80" : a.score >= 50 ? "#FACC15" : "#F87171",
                            display: "inline-flex",
                            alignItems: "center",
                          }}>00</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: "#fff", whiteSpace: "nowrap" as const }}>
                      {fmtDate(a.date)}
                    </div>
                    <ArrowBtn onClick={(e) => {
                      e.stopPropagation();
                      if ((a as any).isScript) {
                        navigate(`/script-details/${a.id}?assignedToId=${a.raw?.repId || repId}`, { state: { from: location.pathname + location.search, selectedRep: rep, repTab: "overview" } });
                      } else if (a.id) {
                        navigate(`/meeting-detail/${a.id}`, { state: { meeting: a.raw, meetingId: a.id, from: location.pathname + location.search, selectedRep: rep, repTab: "overview" } });
                      } else {
                        onSwitchToMeetings();
                      }
                    }} />
                  </div>
                ))
              ) : (
                <div style={{ padding: "24px 0", textAlign: "center", fontSize: 13, color: colors.textMutedLight }}>
                  No activity yet
                </div>
              )}
            </div>
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 16,
                paddingTop: 12,
                borderTop: `1px solid ${colors.divider}`,
              }}
            >
              <span style={{ fontSize: 12, color: colors.textMuted }}>
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, recentActivity.length)} of {recentActivity.length}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button
                  disabled={activityPage === 1}
                  onClick={() => setActivityPage((prev) => Math.max(1, prev - 1))}
                  style={{
                    background: colors.badgeBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: activityPage === 1 ? "not-allowed" : "pointer",
                    color: activityPage === 1 ? colors.textMutedLight : colors.text,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (activityPage !== 1) e.currentTarget.style.background = colors.rowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = colors.badgeBg;
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <span style={{ fontSize: 13, color: colors.text, fontWeight: 600, padding: "0 8px" }}>
                  {activityPage} / {totalPages}
                </span>

                <button
                  disabled={activityPage === totalPages}
                  onClick={() => setActivityPage((prev) => Math.min(totalPages, prev + 1))}
                  style={{
                    background: colors.badgeBg,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 6,
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: activityPage === totalPages ? "not-allowed" : "pointer",
                    color: activityPage === totalPages ? colors.textMutedLight : colors.text,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (activityPage !== totalPages) e.currentTarget.style.background = colors.rowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = colors.badgeBg;
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}

       
        </div>

        {/* Trend Over Time */}
        <div
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 16,
            padding: "20px 22px",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3, marginBottom: 4 }}>
            TREND OVER TIME ({dateFilter === "all" ? "ALL TIME" : dateFilter === "60" ? "LAST 2 MONTHS" : `LAST ${dateFilter} DAYS`})
          </div>
          <TrendChart data={trendData} />
          <div style={{ display: "flex", gap: 16, justifyContent: "space-between", marginTop: 14, flexWrap: "wrap" as const }}>
            {[
              { color: "#4ADE80", label: "Overall Score" },
              { color: "#A78BFA", label: "Skill Score (Avg)" },
              { color: "#38BDF8", label: "AI Score (Avg)" },
              { color: "#FACC15", label: "Practice Sessions" },
            ].map(({ color, label }) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: 15 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: color }} />
                <span style={{ fontSize: 14, color: colors.text }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      

      {/* ── Recommended Next Actions ── */}

      {recommendedActions.length > 0 && (
  <div>
    <div style={{ fontSize: 13, fontWeight: 700, color: colors.text, letterSpacing: 0.3, marginBottom: 14 }}>
      RECOMMENDED NEXT ACTIONS
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
      {recommendedActions.map((a: any, i: number) => (
        <div
          key={i}
          className="action-card"
          style={{
            background: colors.topCardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 14,
            overflow: "hidden",
            transition: "all 0.2s",
          }}
        >
          <div style={{display:"flex" , justifyContent:"space-between"}}>
              <div style={{ padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 12, background: a.iconBg,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, fontSize: 18,
                }}>
                  {a.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: a.color,
                    textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 3,
                  }}>Practice</div>
                  <div style={{
                    fontSize: 15, fontWeight: 700, color: colors.text,
                    textTransform: "capitalize" as const,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const,
                  }}>{a.title}</div>
                </div>
              </div>

              {/* View button */}
              <div style={{ padding: " 18px 16px" , marginTop:"4px" }} >
                <button
                  onClick={() => setActionModalData(a)}
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 10, fontSize: 13, fontWeight: 700,
                    background: `${a.color}18`, border: `1px solid ${a.color}35`,
                    color: a.color, cursor: "pointer", transition: "all 0.2s",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${a.color}30`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = `${a.color}18`; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  View Details
                </button>
              </div>
          </div>

          {/* Why - always visible */}
          {a.why && (
            <div style={{ padding: "0 18px 14px" }}>
              <div style={{
                background: colors.surfaceAlt,
                border: `1px solid ${colors.border}`,
                borderRadius: 10, padding: "10px 12px",
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: a.color, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 5 }}>Why</div>
                <p style={{
                  fontSize: 14, color: colors.text, margin: 0, lineHeight: 1.5,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden",
                }}>{a.why}</p>
              </div>
            </div>
          )}

         
        </div>
      ))}
    </div>
  </div>
)}




     

      {/* Coaching Modal */}
      <CoachingModal
        open={coachOpen}
        onClose={() => setCoachOpen(false)}
        loading={coachLoading}
        error={coachError}
        coachData={coachData}
        repName={rep?.name || "Rep"}
        onGenerate={generateInsights}
      />
      {actionModalData && (
        <ActionDetailModal 
          data={actionModalData} 
          onClose={() => setActionModalData(null)} 
          tenantId={tenantId}
          userId={repId}
          repName={rep?.name}
        />
      )}


    </div>
  );
}

/* ─── Circular Score Gauge ──────────────────────────────────────────────── */
function CircularScore({ score }: { score: number }) {
  const { colors } = useDashboardTheme();
  const r = 28;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(Math.max(score, 0), 100) / 100;
  const dash = circ * pct;
  const color = score >= 70 ? "#4ADE80" : score >= 50 ? "#FACC15" : "#F87171";

  return (
    <div style={{ position: "relative", width: 70, height: 70, flexShrink: 0 }}>
      <svg width="70" height="70" viewBox="0 0 70 70">
        <circle cx="35" cy="35" r={r} fill="none" stroke={colors.ringTrack} strokeWidth="6" />
        <circle
          cx="35"
          cy="35"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          strokeDashoffset={circ * 0.25}
          style={{ transition: "stroke-dasharray 0.8s ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 800, color: colors.text, lineHeight: 1 }}>
          {score > 0 ? score : "—"}
        </span>
        <span style={{ fontSize: 8, color: colors.textMuted, marginTop: 1 }}>/100</span>
      </div>
    </div>
  );
}

/* ─── Stat Card ─────────────────────────────────────────────────────────── */
function StatCard({
  icon, iconBg, label, value, delta, deltaPositive, deltaColor, sub, valueBig, onViewAll,
}: {
  icon: React.ReactNode; iconBg: string; label: string; value: string | number;
  delta?: string; deltaPositive?: boolean; deltaColor?: string; sub?: string; valueBig?: boolean;
  onViewAll?: () => void;
}) {
  const { colors } = useDashboardTheme();
  return (
    <div
      style={{
        background: colors.topCardBg,
        border: `1px solid ${colors.topCardBorder}`,
        borderRadius: 14,
        padding: "16px 18px",
        display: "flexwrap",
        alignItems: "flex-start",
        gap: 14,
        position: "relative",
      }}
    >
      {onViewAll && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewAll();
          }}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: colors.badgeBg,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            color: colors.textMuted,
            fontSize: 10,
            fontWeight: 600,
            padding: "4px 8px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(124, 112, 232, 0.15)";
            e.currentTarget.style.borderColor = "rgba(124, 112, 232, 0.3)";
            e.currentTarget.style.color = "#a78bfa";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = colors.badgeBg;
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.color = colors.textMuted;
          }}
        >
          View All
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 11,
          background: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 16, color: colors.text, marginBottom: 4 ,  fontWeight: 500,}}>{label}</div>
        <div style={{ fontSize: valueBig ? 26 : 24, fontWeight: 800, color: colors.text, lineHeight: 1, marginBottom: 4 }}>
          {value !== undefined && value !== null ? value : "—"}
        </div>
        {delta && (
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: deltaColor || (deltaPositive ? "#4ADE80" : "#F87171"),
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            {deltaPositive && "↑ "}{delta}
            {sub && <span style={{ fontWeight: 400, color: colors.textMuted, marginLeft: 4 }}>{sub}</span>}
          </div>
        )}
        {!delta && sub && (
          <div style={{ fontSize: 13, color: colors.textMuted }}>{sub}</div>
        )}
      </div>
    </div>
  );
}
function Badge({ type }: { type: string }) {
  const { isDark } = useDashboardTheme();
  const s = getBadgeTheme(type, isDark);
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: 13, fontWeight: 600,
      padding: "3px 10px", borderRadius: 20, whiteSpace: "nowrap" as const, letterSpacing: 0.3,
    }}>{s.label}</span>
  );
}
/* ─── Skill Bar ─────────────────────────────────────────────────────────── */
function SkillBar({ label, value, icon }: { label: string; value: number; icon: string }) {
  const { colors, isDark } = useDashboardTheme();
  const pct = Math.min(value, 100);
  const color = pct >= 70 ? "#4ADE80" : pct >= 50 ? "#FACC15" : "#F87171";
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {/* Icon */}
        <span style={{ fontSize: 15, opacity: isDark ? 0.7 : 1, flexShrink: 0, filter: isDark ? "grayscale(1) brightness(2)" : undefined }}>
          {icon}
        </span>
        {/* Skill name */}
        <span style={{ fontSize: 15, color: colors.text, fontWeight: 400, minWidth: 130, flexShrink: 0 }}>
          {label}
        </span>
        {/* Bar */}
        <div style={{ marginLeft: 8, flex: 1, height: 8, background: colors.ringTrack, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.7s ease" }} />
        </div>
        {/* Score */}
        <span style={{ fontSize: 15, fontWeight: 600, color: colors.text, minWidth: 24, textAlign: "right" as const }}>
          {Math.round(value)}
        </span>
      </div>
      <div style={{ height: 1, background: colors.divider }} />
    </>
  );
}

/* ─── Trend Chart (SVG) ─────────────────────────────────────────────────── */
function TrendChart({ data }: { data: TrendPoint[] }) {
  const { colors, isDark } = useDashboardTheme();
  
  if (!data || data.length < 2) {
    return (
      <div
        style={{
          height: 160,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: colors.textMuted,
          fontSize: 13,
        }}
      >
        Not enough data to render trend
      </div>
    );
  }

  const W = 420;
  const H = 160;
  const PAD = { top: 12, right: 16, bottom: 32, left: 30 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const allVals = data.flatMap((d) => [d.overall, d.skillAvg, d.aiScore]);
  const minV = Math.max(0, Math.min(...allVals) - 10);
  const maxV = Math.min(100, Math.max(...allVals) + 10);

  const xPos = (i: number) => PAD.left + (i / (data.length - 1)) * cW;
  const yPos = (v: number) => PAD.top + cH - ((v - minV) / (maxV - minV)) * cH;

  const maxPractice = Math.max(...data.map((d) => d.practice), 1);

  const yVal = (d: TrendPoint, key: "overall" | "skillAvg" | "aiScore" | "practice") => {
    if (key === "practice") {
      const pct = maxPractice > 0 ? d.practice / maxPractice : 0;
      const equivalentValue = minV + pct * (maxV - minV) * 0.8;
      return yPos(equivalentValue);
    }
    return yPos(d[key]);
  };

  const lineFor = (key: "overall" | "skillAvg" | "aiScore" | "practice") =>
    data
      .map((d, i) => `${i === 0 ? "M" : "L"} ${xPos(i)} ${yVal(d, key)}`)
      .join(" ");

  const lines = [
    { key: "overall" as const, color: isDark ? "#4ADE80" : "#16A34A" },
    { key: "skillAvg" as const, color: isDark ? "#A78BFA" : "#4F46E5" },
    { key: "aiScore" as const, color: isDark ? "#38BDF8" : "#0284C7" },
    { key: "practice" as const, color: isDark ? "#FACC15" : "#D97706" },
  ];

  const gridY = [25, 50, 75, 100].filter((v) => v >= minV && v <= maxV);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      style={{ width: "100%", height: 300, overflow: "visible" }}
    >
      {gridY.map((v) => (
        <g key={v}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={yPos(v)}
            y2={yPos(v)}
            stroke={colors.divider}
            strokeWidth="1"
          />
          <text
            x={PAD.left - 4}
            y={yPos(v) + 4}
            fontSize="9"
            fill={colors.textMuted}
            textAnchor="end"
          >
            {v}
          </text>
        </g>
      ))}

      {lines.map(({ key, color }) => (
        <path
          key={key}
          d={lineFor(key)}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}

      {lines.map(({ key, color }) =>
        data.map((d, i) => (
          <circle
            key={`${key}-${i}`}
            cx={xPos(i)}
            cy={yVal(d, key)}
            r="3"
            fill={color}
          />
        ))
      )}

      {data.map((d, i) => (
        <text
          key={i}
          x={xPos(i)}
          y={H - 6}
          fontSize="9"
          fill={colors.textMuted}
          textAnchor="middle"
        >
          {d.label}
        </text>
      ))}
    </svg>
  );
}

/* ─── Coaching Modal ─────────────────────────────────────────────────────── */
interface CoachingModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: string | null;
  coachData: any;
  repName: string;
  onGenerate: () => void;
}

function CoachingModal({
  open, onClose, loading, error, coachData, repName, onGenerate,
}: CoachingModalProps) {
  if (!open) return null;
  const insights = coachData?.insights;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "rgba(0,0,0,0.88)",
        backdropFilter: "blur(16px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 660,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 24,
          overflow: "hidden",
          background: "linear-gradient(160deg, #080810 0%, #060608 60%, #0a080f 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 40px 80px rgba(0,0,0,0.9)",
        }}
      >
        {/* Header */}
        <div style={{ padding: "22px 24px 16px", display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: 14, flexShrink: 0,
              background: "linear-gradient(135deg, rgba(184,248,144,0.15), rgba(184,248,144,0.05))",
              border: "1px solid rgba(184,248,144,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.14em", color: "rgba(184,248,144,0.6)", margin: "0 0 2px", fontWeight: 700 }}>
              AI Coaching Intelligence
            </p>
            <p style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {repName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", cursor: "pointer", color: "#fff" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div style={{ height: 1, background: "rgba(255,255,255,0.06)", flexShrink: 0 }} />

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 }}>
              <div style={{ width: 52, height: 52, borderRadius: "50%", border: "2px solid rgba(184,248,144,0.2)", borderTopColor: "#B8F890", animation: "spin 1s linear infinite" }} />
              <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: 16, fontWeight: 600, color: "#fff", margin: "0 0 6px" }}>Generating Insights</p>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", margin: 0 }}>Analysing pitches & patterns… (30–40s)</p>
              </div>
            </div>
          )}
          {!loading && error && (
            <div style={{ borderRadius: 14, border: "1px solid rgba(248,113,113,0.25)", background: "rgba(248,113,113,0.06)", padding: "16px 20px" }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: "#fca5a5", margin: "0 0 4px" }}>Generation Failed</p>
              <p style={{ fontSize: 14, color: "rgba(252,165,165,0.7)", margin: 0 }}>{error}</p>
            </div>
          )}
          {!loading && !error && insights && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {(insights.pitchAnalysis || insights.overallAssessment) && (
                <InsightSection
                  label="Pitch Analysis"
                  color="#a78bfa"
                  value={insights.pitchAnalysis || insights.overallAssessment}
                />
              )}
              {insights.meetingAnalysis && (
                <InsightSection
                  label="Meeting Analysis"
                  color="#a78bfa"
                  value={insights.meetingAnalysis}
                />
              )}
              {insights.quickWin && <InsightSection label="Quick Win" color="#34d399" value={insights.quickWin} />}
              {insights.managerTip && <InsightSection label="Manager Tip" color="#38bdf8" value={insights.managerTip} />}
              {insights.objectionCoaching && <InsightSection label="Objection Coaching" color="#f59e0b" value={insights.objectionCoaching} />}
              {Array.isArray(insights.priorityFocusAreas) && insights.priorityFocusAreas.length > 0 && (
                <div>
                  <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.14em", fontWeight: 700, color: "#fff", margin: "0 0 12px" }}>Priority Focus Areas</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {insights.priorityFocusAreas.map((fa: any, idx: number) => {
                      const cols = ["#a78bfa", "#38bdf8", "#f59e0b"];
                      const col = cols[idx % cols.length];
                      return (
                        <div key={idx} style={{ background: `${col}08`, border: `1px solid ${col}25`, borderRadius: 14, padding: "14px 18px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: fa.why || fa.specificAction ? 10 : 0 }}>
                            <span style={{ width: 26, height: 26, borderRadius: "50%", background: `${col}20`, color: col, border: `1px solid ${col}30`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                              {idx + 1}
                            </span>
                            <p style={{ fontSize: 14, fontWeight: 700, color: "#fff", margin: 0, textTransform: "capitalize" }}>{fa.area}</p>
                          </div>
                          {fa.why && <p style={{ fontSize: 13, color: "rgba(255,255,255,0.75)", margin: "0 0 8px", paddingLeft: 36, lineHeight: 1.5 }}><span style={{ color: `${col}99`, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 6 }}>Why</span>{fa.why}</p>}
                          {fa.specificAction && <div style={{ background: `${col}08`, border: `1px solid ${col}15`, borderRadius: 10, padding: "10px 14px", marginLeft: 36 }}><p style={{ fontSize: 13, color: "#d1d5db", margin: 0, lineHeight: 1.5 }}><span style={{ color: col, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 6 }}>Action</span>{fa.specificAction}</p></div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {!loading && !error && !insights && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 20px", gap: 16 }}>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", margin: 0 }}>No insights available yet.</p>
              <button
                onClick={onGenerate}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 22px", borderRadius: 12, fontSize: 14, fontWeight: 700, background: "linear-gradient(135deg, rgba(184,248,144,0.18), rgba(184,248,144,0.05))", color: "#B8F890", border: "1px solid rgba(184,248,144,0.3)", cursor: "pointer" }}
              >
                Generate Coaching Insights
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid rgba(255,255,255,0.06)", flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#B8F890" }} />
            <span style={{ fontSize: 12, textTransform: "uppercase", color: "rgba(255,255,255,0.5)", fontWeight: 600 }}>Powered by AI · Pitchster</span>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "#fff", padding: "7px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function InsightSection({ label, color, value }: { label: string; color: string; value: string }) {
  return (
    <div style={{ background: `${color}0a`, border: `1px solid ${color}30`, borderRadius: 16, padding: "16px 20px" }}>
      <p style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, color, margin: "0 0 8px" }}>{label}</p>
      <p style={{ fontSize: 14, color: "#e5e7eb", margin: 0, lineHeight: 1.6 }}>{value}</p>
    </div>
  );
}


function parseScoreItems(text: string) {
  const hasUnderscoreLabel = /[a-zA-Z]+_[a-zA-Z_]+/i.test(text);
  if (!hasUnderscoreLabel) return [];

  const items: { label: string; score: number }[] = [];
  const seen = new Set<string>();

  const formatLabel = (rawLabel: string) =>
    rawLabel
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const pushItem = (rawLabel: string, score: number) => {
    if (!rawLabel.includes("_") || Number.isNaN(score)) return;
    const label = formatLabel(rawLabel);
    const key = label.toLowerCase();
    if (label.length > 1 && label.length < 40 && !seen.has(key)) {
      seen.add(key);
      items.push({ label, score });
    }
  };

  const sharedRegex = /(\d+)\s*%\s*\(([^)]+)\)/g;
  let sharedMatch;
  while ((sharedMatch = sharedRegex.exec(text)) !== null) {
    const score = parseInt(sharedMatch[1], 10);
    sharedMatch[2].split(",").forEach((part) => {
      const rawLabel = part.trim();
      if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(rawLabel)) pushItem(rawLabel, score);
    });
  }

  const skillRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b(?:\s+(?:is|at|and))?\s*(\d+)(?:%|\/100)?/g;
  let match;
  while ((match = skillRegex.exec(text)) !== null) {
    pushItem(match[1].trim(), parseInt(match[2], 10));
  }

  return items;
}

function ScoreListContent({ text, colors }: { text: string; colors: any }) {
  const skills = parseScoreItems(text);
  const introText = skills.length > 0 ? "" : text.split("(")[0].trim();

  const scoreColor = (s: number) =>
    s >= 70 ? "#4ADE80" : s >= 50 ? "#FACC15" : "#F87171";
  const scoreBg = (s: number) =>
    s >= 70 ? "rgba(74,222,128,0.1)" : s >= 50 ? "rgba(250,204,21,0.08)" : "rgba(248,113,113,0.1)";

  return (
    <div>
      {introText && (
        <p style={{ fontSize: 16, color: colors.text, margin: "0 0 10px", lineHeight: 1.5 }}>
          {introText}
        </p>
      )}

      {skills.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {skills.map((s, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center",
              justifyContent: "space-between", gap: 10,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                <div style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: scoreColor(s.score), flexShrink: 0,
                }} />
                <span style={{ fontSize: 14, color: colors.text, fontWeight: 500 }}>
                  {s.label}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{
                  width: 70, height: 5,
                  background: colors.ringTrack,
                  borderRadius: 3, overflow: "hidden",
                }}>
                  <div style={{
                    width: `${s.score}%`, height: "100%",
                    background: scoreColor(s.score),
                    borderRadius: 3,
                    transition: "width 0.6s ease",
                  }} />
                </div>
                <span style={{
                  fontSize: 14, fontWeight: 700,
                  padding: "2px 8px", borderRadius: 999,
                  background: scoreBg(s.score),
                  color: scoreColor(s.score),
                  minWidth: 38, textAlign: "center" as const,
                  border: `1px solid ${scoreColor(s.score)}30`,
                }}>
                  {s.score}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WhyContent({ text, colors }: { text: string; colors: any }) {
  return <ScoreListContent text={text} colors={colors} />;
}

const scenarioMatchClientCache = new Map<string, any>();

function ActionDetailModal({ 
  data, 
  onClose,
  tenantId,
  userId,
  repName,
}: { 
  data: any; 
  onClose: () => void; 
  tenantId?: string;
  userId?: string;
  repName?: string;
}) {
  const { isDark, colors } = useDashboardTheme();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [matchResult, setMatchResult] = useState<any | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [isAssigned, setIsAssigned] = useState(false);
  const [resolvedScenarioId, setResolvedScenarioId] = useState<string>("");

  const resolvedUserId = userId || (data as any)?.userId || (data as any)?.repId || (data as any)?.user?.id || "";

  const getScenarioIdFromMatchResult = (result: any) => {
    const directId = result?.scenarioId || result?.id || result?.data?.scenarioId || result?.data?.id;
    if (directId) return directId;

    const nestedScenario = result?.scenario || result?.data?.scenario;
    return nestedScenario?.id || nestedScenario?.scenarioId || nestedScenario?.scenario_id;
  };

  const resolveScenarioId = async (result: any, fallbackTitle: string) => {
    const fromResult = getScenarioIdFromMatchResult(result);
    if (fromResult) return fromResult;

    if (!fallbackTitle) return "";

    try {
      const list = await scenarioService.getScenarios({ search: fallbackTitle, page: 1, limit: 10 });
      const matchingScenario = list?.scenarios?.find((scenario: any) => {
        const title = (scenario?.title || "").toLowerCase();
        const target = fallbackTitle.toLowerCase();
        return title === target || title.includes(target) || target.includes(title);
      });

      return matchingScenario?.id || "";
    } catch (error) {
      console.error("Failed to resolve scenario id for assignment", error);
      return "";
    }
  };

  const diffColor = (scenario: string) => {
    if (/HARD/i.test(scenario)) return { color: "#F87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.3)" };
    if (/MEDIUM/i.test(scenario)) return { color: "#FACC15", bg: "rgba(250,204,21,0.1)", border: "rgba(250,204,21,0.28)" };
    return { color: "#4ADE80", bg: "rgba(74,222,128,0.1)", border: "rgba(74,222,128,0.28)" };
  };
  const getDiffLabel = (scenario: string) => {
    if (/HARD/i.test(scenario)) return "HARD";
    if (/MEDIUM/i.test(scenario)) return "MEDIUM";
    return "EASY";
  };

  const scenarioName = data?.recommendedScenario?.split("(")[0]?.trim() || "";
  const diff = diffColor(data?.recommendedScenario || "");
  const diffLabel = getDiffLabel(data?.recommendedScenario || "");
  const recommendedTitle = data?.recommendedScenario;
  const assignableScenarioId = resolvedScenarioId || getScenarioIdFromMatchResult(matchResult);
  const canAssignScenario = Boolean(assignableScenarioId && resolvedUserId);

  const handleDirectAssign = async () => {
    let scenarioId = getScenarioIdFromMatchResult(matchResult);

    if (!scenarioId) {
      scenarioId = await resolveScenarioId(matchResult, scenarioName);
    }

    if (!scenarioId || !resolvedUserId) {
      toast.error("Unable to assign this scenario to the selected rep right now.");
      return;
    }

    setAssigning(true);
    let lastError: any = null;

    try {
      const payloadVariants = [
        { userIds: [resolvedUserId], teamIds: [] },
        { userIds: [resolvedUserId], teamIds: [], tenantId },
        { userId: resolvedUserId, teamIds: [] },
        { userId: resolvedUserId, tenantId },
      ];

      for (const payload of payloadVariants) {
        try {
          await scenarioService.assignScenario(scenarioId, payload as any);
          setIsAssigned(true);
          toast.success("Assigned successfully!");
          return;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError;
    } catch (error) {
      console.error("Failed to assign scenario", error);
      const backendMessage =
        (error as any)?.response?.data?.message ||
        (error as any)?.response?.data?.error ||
        (error as any)?.message ||
        "Failed to assign scenario.";
      toast.error(backendMessage);
    } finally {
      setAssigning(false);
    }
  };

  useEffect(() => {
    if (!recommendedTitle || !tenantId || !data) return;

    const cacheKey = `${tenantId}_${userId || ""}_${data.area || ""}_${recommendedTitle}`;

    const applyPayload = async (payload: any) => {
      setMatchResult(payload);
      const scenarioIdFromMatch = getScenarioIdFromMatchResult(payload);
      let nextScenarioId = scenarioIdFromMatch || "";

      if (!nextScenarioId) {
        nextScenarioId = await resolveScenarioId(payload, scenarioName);
      }

      setResolvedScenarioId(nextScenarioId);

      const isAssignedFromPayload = Boolean(
        payload.isAssigned ||
        payload.assigned ||
        payload.isAssignedToUser ||
        payload.assignedToUser ||
        payload.assignedUserIds?.includes?.(resolvedUserId) ||
        payload.userIds?.includes?.(resolvedUserId)
      );

      if (isAssignedFromPayload || !resolvedUserId) {
        setIsAssigned(isAssignedFromPayload);
        return;
      }

      if (nextScenarioId && resolvedUserId) {
        try {
          const assignments = await scenarioService.getScenarioAssignments(nextScenarioId);
          const assignedUserIds = assignments?.userIds || [];
          setIsAssigned(assignedUserIds.includes(resolvedUserId));
        } catch (assignmentError) {
          console.error("Failed to fetch scenario assignments", assignmentError);
          setIsAssigned(false);
        }
      }
    };

    if (scenarioMatchClientCache.has(cacheKey)) {
      const cached = scenarioMatchClientCache.get(cacheKey);
      setChecking(false);
      applyPayload(cached);
      return;
    }

    setChecking(true);
    setMatchResult(null);
    setResolvedScenarioId("");
    setIsAssigned(false);

    checkScenarioMatch({
      weakArea: data.area || "",
      recommendedTitle,
      tenantId,
      userId,
    })
      .then(async (res: any) => {
        const payload = res?.data ?? res;
        if (payload) {
          scenarioMatchClientCache.set(cacheKey, payload);
          await applyPayload(payload);
        }
      })
      .catch((err) => {
        console.error("Failed to check scenario match:", err);
      })
      .finally(() => {
        setChecking(false);
      });
  }, [recommendedTitle, tenantId, data?.area, userId, resolvedUserId, scenarioName]);

  if (!data) return null;

  return (
    <>
      <style>{`
        @keyframes shimmerPulse {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
      <div
      style={{
        position: "fixed", inset: 0, zIndex: 60,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, background: isDark ? "rgba(0,0,0,0.8)" : "rgba(0,0,0,0.45)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        width: "100%", maxWidth: 520,
        borderRadius: 24, overflow: "hidden",
        background: isDark ? "linear-gradient(160deg, #080810 0%, #060608 60%, #0a080f 100%)" : "#FFFFFF",
        border: `1px solid ${colors.border}`,
        boxShadow: "0 40px 80px rgba(0,0,0,0.3)",
        animation: "fadeIn 0.2s ease",
      }}>
        {/* Header */}
        <div style={{ padding: "22px 24px 16px", display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 14, flexShrink: 0,
            background: data.iconBg,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
          }}>
            {data.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: data.color, margin: "0 0 3px", fontWeight: 700 }}>
              Practice · Focus Area
            </p>
            <p style={{ fontSize: 17, fontWeight: 800, color: colors.text, margin: 0, textTransform: "capitalize" }}>
              {data.title}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: colors.badgeBg, border: `1px solid ${colors.border}`,
              cursor: "pointer", color: colors.text,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div style={{ height: 1, background: colors.divider }} />

        {/* Body */}
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Why */}
          {data.why && (
            <div style={{
              background: colors.surfaceAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: data.color, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 6 }}>Why</div>
            <WhyContent text={data.why} colors={colors} />
            </div>
          )}

          {/* Action */}
          {data.specificAction && (
            <div style={{
              background: `${data.color}08`,
              border: `1px solid ${data.color}22`,
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: data.color, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 6 }}>Action</div>
              <p style={{ fontSize: 16, color: colors.text, margin: 0, lineHeight: 1.6 }}>{data.specificAction}</p>
            </div>
          )}

          {/* Evidence */}
          {data.evidence && (
            <div style={{
              background: colors.surfaceAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.1em", marginBottom: 6 }}>Evidence</div>
              <ScoreListContent text={data.evidence} colors={colors} />
            </div>
          )}

          {/* Recommended Scenario */}
          {data.recommendedScenario && (
            <div style={{
              background: colors.surfaceAlt,
              border: `1px solid ${colors.border}`,
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ display: "flex", gap: "14px", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, textTransform: "uppercase" as const, letterSpacing: "0.1em" }}>
                  Practice Scenario
                </div>
                <span style={{
                    fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: "6px",
                    background: diff.bg, color: diff.color, border: `1px solid ${diff.border}`,
                    letterSpacing: "0.08em", flexShrink: 0,
                  }}>{diffLabel}</span>
               
              </div>
              
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" as const }}>
                {checking ? (
                  <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{
                      flex: 1,
                      minWidth: 0,
                      padding: "12px 14px",
                      borderRadius: 12,
                      background: colors.badgeBg,
                      border: `1px solid ${colors.border}`,
                      display: "flex",
                      flexDirection: "column" as const,
                      gap: 8,
                    }}>
                      <div style={{
                        height: 12,
                        width: "72%",
                        borderRadius: 999,
                        background: colors.shimmerGrad,
                        backgroundSize: "200% 100%",
                        animation: "shimmerPulse 1.2s linear infinite",
                      }} />
                      <div style={{
                        height: 10,
                        width: "45%",
                        borderRadius: 999,
                        background: colors.shimmerGrad,
                        backgroundSize: "200% 100%",
                        animation: "shimmerPulse 1.2s linear infinite",
                      }} />
                      <div style={{
                        height: 10,
                        width: "28%",
                        borderRadius: 999,
                        background: colors.shimmerGrad,
                        backgroundSize: "200% 100%",
                        animation: "shimmerPulse 1.2s linear infinite",
                      }} />
                    </div>
                  </div>
                ) : (
                  <span style={{ fontSize: 14, color: colors.text, fontWeight: 600, flex: 1, minWidth: 0 }}>
                    {scenarioName}
                  </span>
                )}

                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {!checking && (
                    <>
                      {canAssignScenario ? (
                        <button
                          onClick={isAssigned || assigning ? undefined : handleDirectAssign}
                          disabled={assigning}
                          style={{
                            flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
                            padding: "6px 14px", borderRadius: 8,
                            background: isAssigned 
                              ? "rgba(74,222,128,0.15)" 
                              : (isDark ? "linear-gradient(135deg, rgba(184,248,144,0.2), rgba(184,248,144,0.05))" : "linear-gradient(135deg, rgba(34,197,94,0.2), rgba(34,197,94,0.05))"),
                            border: isAssigned 
                              ? "1px solid rgba(74,222,128,0.4)" 
                              : (isDark ? "1px solid rgba(184,248,144,0.45)" : "1px solid rgba(34,197,94,0.45)"),
                            color: isAssigned ? (isDark ? "#4ADE80" : "#15803D") : (isDark ? "#B8F890" : "#15803D"),
                            fontSize: 12, fontWeight: 700, 
                            cursor: isAssigned ? "default" : assigning ? "not-allowed" : "pointer", 
                            transition: "all 0.2s",
                          }}
                        >
                          {assigning ? (
                            <div style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,0.2)", borderTopColor: isDark ? "#B8F890" : "#15803D", borderRadius: "50%", animation: "spin 1s linear infinite", marginRight: 2 }} />
                          ) : isAssigned ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ marginRight: 2 }}><path d="M20 6L9 17l-5-5" /></svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 2 }}><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="17" y1="11" x2="23" y2="11"/></svg>
                          )}
                          {assigning ? "Assigning..." : isAssigned ? "Assigned" : "Assign Scenario"}
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            navigate("/practice-engine", {
                              state: {
                                showCreate: true,
                                prefilledFields: matchResult?.aiGeneratedFields,
                                returnTo: window.location.pathname,
                                repContext: { id: resolvedUserId, name: repName || "Rep" },
                              },
                            });
                            onClose();
                          }}
                          style={{
                            flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
                            padding: "6px 14px", borderRadius: 8,
                            background: "linear-gradient(135deg, rgba(124,112,232,0.25), rgba(124,112,232,0.1))",
                            border: "1px solid rgba(124,112,232,0.4)",
                            color: "#A78BFA",
                            fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 2 }}><path d="M12 5v14M5 12h14"/></svg>
                          Create Scenario
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: `1px solid ${colors.divider}`, display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              background: colors.badgeBg, border: `1px solid ${colors.border}`,
              color: colors.text, padding: "8px 22px", borderRadius: 999,
              fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}
          >Close</button>
        </div>
      </div>
      </div>
    </>
  );
}